/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Stack,
  TextField,
  IconButton,
  Button,
  useTheme,
  useMediaQuery,
  Tooltip,
  Chip,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchTwoToneIcon from '@mui/icons-material/SearchTwoTone';
import { KeyboardEvent, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import {
  initialState,
  reducerUpdateAdvanceClear,
  reducerUpdateAdvanceSearch,
  reducerUpdateAdvanceSearchCollapseRole,
  reducerUpdateAdvanceSearchCreatorType,
  reducerUpdateAdvanceSearchIncludeKeywords,
  reducerUpdateAdvanceSearchIsCollab
} from 'src/features/advanceSearchSocialMedia';
import AdvanceSearchSocialMedia from 'src/components/AdvanceSearchSocialMedia';
import ClearIcon from '@mui/icons-material/Clear';
import changeObjectToQuery from 'src/utils/changeObjectToQuery';
import FilterListIcon from '@mui/icons-material/FilterList';
import TooltipCustomize from 'src/components/TooltipCustomize';
import SelectDateRangeRetail from 'src/components/Retail/SelectDateRangeRetail';
import { RestoreFromTrash } from '@mui/icons-material';
import { isNoDuplicateVersion } from 'src/App';
import SelectCollaborationStatus from 'src/components/SelectCollaborationStatus';
import SelectCreatorType from 'src/components/SelectCreatorType';
import SelectCollaborationType from 'src/components/SelectCollaborationType';
import RefreshIcon from '@mui/icons-material/Refresh';
import { ECOMMERCE_SOURCES } from 'src/constants';
import { reducerUpdateAnalyticArgumentObjectIdList } from 'src/features/analyticArguments';
import { reducerUpdateObjectListByObjectType } from 'src/features/objectManagement';
import {
  reducerUpdateStreamSosmedSelected,
  reducerUpdateIsSelectedAllPrimary
} from 'src/features/sosmedStream';
import { concatUniqueObjectsById } from 'src/utils/concatUniqueObjectsById';
import { useAlert } from 'src/hooks/useAlert';
import useObjectManagement from 'src/services/useObjectManagement';
import { t } from 'i18next';

const CustomBox = styled(Box)(
  ({ theme }) => `
    // width: calc(100vw - ${theme.sidebar.width} - ${theme.spacing(10)});
    margin: ${theme.spacing(2, 0)};
    // background: #fbfbfb;
    // position: absolute;
`
);

const FilterBox = styled(Box)(
  ({ theme }) => `
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`
);

const ChipCustom = styled(Chip)(
  ({ theme }) => `
    background-color: ${theme.palette.primary.light};
    border-radius: ${theme.spacing(1)};
    padding: 10px ${theme.spacing(2)};
    height: 29px;
    width: 100px;
    .MuiChip-label {
        color: #0A789D;
        font-size: 14px;
        line-height: 130%;
        margin: 0 4px;
    }
    .MuiChip-deleteIcon {
        font-size: 14px;
        color: #0A789D;
    }
  `
);

function BottomHeader() {
  const dispatch = useAppDispatch();
  const advanceSearch = useAppSelector(
    (state) => state.storeAdvanceSearchSocialMedia
  );
  const project = useAppSelector((state) => state.users.project);
  const timeframe = useAppSelector(
    (state) => state.analyticArguments.dateRange
  );
  const { groupSelectedById: selectedGroupId, selectedGroups } = useAppSelector(
    (state) => state.objectManagement
  );
  const {
    streamsCommentingToPost,
    selectedOriginalToPost,
    searchOriginalToPost,
    ...rest
  } = advanceSearch;

  const { handleClickAlert } = useAlert();
  const { getObjectList, getGroupObject } = useObjectManagement();

  const query = changeObjectToQuery(rest);
  const [localSearch, setLocalSearch] = useState<string>('');

  const advanceSearchKeys = query
    ? query.split(';').slice(0, query.split(';').length - 1)
    : [];

  const { objectIdList } = useAppSelector((state) => state.analyticArguments);

  const objectList = useAppSelector(
    (state) => state.objectManagement.objectList.data
  );

  const showCollaborationFilters =
    isNoDuplicateVersion(project, timeframe.start) && selectedGroups.length > 0;
  const collaborationStatus = advanceSearch.collabRole ?? 'all';
  const creatorType = advanceSearch.creatorType ?? 'all';
  const isCollab =
    advanceSearch.isCollab === undefined
      ? 'all'
      : advanceSearch.isCollab
      ? 'collab'
      : 'non-collab';

  const handleChangeCollaborationStatus = (value: string) => {
    dispatch(
      reducerUpdateAdvanceSearchCollapseRole(
        value === 'all' ? undefined : (value as any)
      )
    );
  };

  const handleChangeCreatorType = (value: string) => {
    dispatch(
      reducerUpdateAdvanceSearchCreatorType(
        value === 'all' ? undefined : (value as any)
      )
    );
  };

  const handleChangeIsCollab = (value: string) => {
    dispatch(
      reducerUpdateAdvanceSearchIsCollab(
        value === 'all' ? undefined : value === 'collab' ? true : false
      )
    );
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') handleSearch();
  };

  const handleClear = () => {
    setLocalSearch('');
    dispatch(reducerUpdateAdvanceClear(initialState));
  };

  const handleSearch = () => {
    dispatch(reducerUpdateAdvanceSearchIncludeKeywords([localSearch]));
    setLocalSearch('');
  };

  const handleDeleteFilter = (item: string) => {
    const splitFilter = item.split(':');
    const deletedFilter = splitFilter[0].trim();
    dispatch(
      reducerUpdateAdvanceSearch({
        ...advanceSearch,
        [deletedFilter]: initialState[deletedFilter]
      })
    );
  };

  const handleRefresh = () => {
    dispatch(reducerUpdateStreamSosmedSelected([]));
    dispatch(reducerUpdateIsSelectedAllPrimary(false));
    Promise.all([
      getObjectList(project.id),
      getGroupObject({ projectId: project.id, templateName: 'content' })
    ])
      .then((responses) => {
        // if a group is selected
        if (selectedGroupId !== '_default' || selectedGroups.length > 0) {
          const newGroupObjectList = responses[1].data;
          const prevSelectedGroups = selectedGroups;

          const prevSelectedGroupIds = prevSelectedGroups
            .map((group) => group.objectList.map((group) => group.id))
            .flat();

          const newSelectedGroups = newGroupObjectList.filter((group) =>
            prevSelectedGroups.some((prevGroup) => prevGroup.id === group.id)
          );

          const allObjectsFromGroups = newSelectedGroups.flatMap(
            (group) => group.objectList
          );

          // semua object yang ada di active object + object baru di grup
          const finalActiveObjects = allObjectsFromGroups.filter(
            (object) =>
              objectIdList.includes(object.id) ||
              !prevSelectedGroupIds.includes(object.id)
          );
          // dispatch(reducerUpdateObjectListByObjectType(finalActiveObjects));
          dispatch(
            reducerUpdateAnalyticArgumentObjectIdList(
              finalActiveObjects.map((object) => object.id)
            )
          );
          // if no group selected
        } else {
          const newObjectList = responses[0].data;

          // Filter out ecommerce sources
          const filteredNewObjectList = newObjectList.filter(
            (object) => !ECOMMERCE_SOURCES.includes(object.socialMedia as any)
          );

          // get only new objects
          const newObjects = filteredNewObjectList.filter((object) => {
            const prevObjectListIds = objectList.map((object) => object.id);
            return !prevObjectListIds.includes(object.id);
          });

          const selectedObjects = filteredNewObjectList.filter((object) =>
            objectIdList.includes(object.id)
          );
          const finalActiveObjects = concatUniqueObjectsById(
            selectedObjects,
            newObjects
          );

          // dispatch(reducerUpdateObjectListByObjectType(finalActiveObjects));
          dispatch(
            reducerUpdateAnalyticArgumentObjectIdList(
              finalActiveObjects.map((object) => object.id)
            )
          );
        }
      })
      .catch((error) =>
        handleClickAlert({
          vertical: 'top',
          horizontal: 'center',
          message: "Couldn't fetch data, please try again.",
          severity: 'error'
        })
      );
  };

  const theme = useTheme();
  const matchesUpMd = useMediaQuery(theme.breakpoints.up('md'));

  const [open, setOpen] = useState<boolean>(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDeleteAllFilter = () => {
    dispatch(reducerUpdateAdvanceSearch(initialState));
  };

  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));

  const FilterSection = () => {
    return (
      <>
        <Stack
          direction="row"
          overflow="auto"
          maxWidth={400}
          spacing={1}
          sx={{
            scrollbarWidth: 'thin'
          }}
        >
          {advanceSearchKeys
            ? advanceSearchKeys.map((item) => {
                return (
                  <Tooltip title={item}>
                    <ChipCustom
                      variant="filled"
                      deleteIcon={<ClearIcon />}
                      onDelete={() => handleDeleteFilter(item)}
                      label={item}
                      size="small"
                    />
                  </Tooltip>
                );
              })
            : null}
        </Stack>
        {advanceSearchKeys.length > 0 ? (
          <Stack
            direction="row"
            alignItems="center"
            sx={{ cursor: 'pointer' }}
            onClick={handleDeleteAllFilter}
          >
            <RestoreFromTrash fontSize="small" />
            <Typography fontSize="14px">{t('clear filters')}</Typography>
          </Stack>
        ) : null}
        {showCollaborationFilters && (
          <Stack direction="row" spacing={1} sx={{ ml: 1 }}>
            <SelectCollaborationType
              value={isCollab}
              onChange={handleChangeIsCollab}
            />
            <SelectCollaborationStatus
              value={collaborationStatus}
              onChange={handleChangeCollaborationStatus}
            />
            <SelectCreatorType
              value={creatorType}
              onChange={handleChangeCreatorType}
            />
          </Stack>
        )}
      </>
    );
  };

  return (
    <CustomBox>
      <FilterBox
        flexDirection={{ xs: 'column', md: 'row' }}
        gap={{ xs: 1 }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent={{ xs: 'flex-start' }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          maxWidth={isLargeScreen ? '400px' : 'unset'}
        >
          <TextField
            value={localSearch}
            size="small"
            InputProps={{
              startAdornment: (
                <IconButton
                  size="small"
                  onClick={handleSearch}
                  sx={{ backgroundColor: 'white' }}
                >
                  <SearchTwoToneIcon />
                </IconButton>
              ),
              endAdornment: localSearch ? (
                <IconButton size="small" onClick={handleClear}>
                  <ClearIcon />
                </IconButton>
              ) : null,
              sx: { borderRadius: '16px', backgroundColor: 'white' }
            }}
            placeholder={t('search stream')}
            onChange={(e) => setLocalSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            fullWidth
          />
          <SelectDateRangeRetail
            selectProps={{
              sx: {
                borderRadius: '16px',
                width: '200px'
              }
            }}
          />
        </Stack>
        <Stack
          direction="row"
          justifyContent={isLargeScreen ? 'unset' : 'space-between'}
          width={'100%'}
          spacing={1}
          alignItems="center"
        >
          <TooltipCustomize title={t('advance search')} placement="top-start">
            <Button
              variant="contained"
              sx={{
                flex: matchesUpMd ? 'unset' : 1,
                borderRadius: '16px',
                height: theme.spacing(5),
                minWidth: theme.spacing(6),
                background: theme.palette.primary.main
              }}
              onClick={() => handleClickOpen()}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <FilterListIcon htmlColor="#FFFFFF" />
                <Typography color="white" fontWeight={700}>
                  {t('filter')}
                </Typography>
              </Stack>
            </Button>
          </TooltipCustomize>
          {open && (
            <AdvanceSearchSocialMedia open={open} onClose={handleClose} />
          )}
          <Box display={matchesUpMd ? 'none' : 'unset'}>
            <TooltipCustomize title="Refresh all metric" placement="top-start">
              <Button
                variant="contained"
                onClick={handleRefresh}
                sx={{
                  borderRadius: '6px',
                  height: theme.spacing(5),
                  minWidth: theme.spacing(6),
                  background: theme.palette.primary.main
                }}
              >
                <RefreshIcon htmlColor={theme.colors.primary.lighter} />
              </Button>
            </TooltipCustomize>
          </Box>
          {matchesUpMd ? <FilterSection /> : null}
        </Stack>
        {!matchesUpMd ? <FilterSection /> : null}
        <Box
          flexGrow={1}
          textAlign={'right'}
          display={matchesUpMd ? 'unset' : 'none'}
        >
          <TooltipCustomize
            title={t('refresh all metric')}
            placement="top-start"
          >
            <Button
              variant="contained"
              onClick={handleRefresh}
              sx={{
                borderRadius: '6px',
                height: theme.spacing(5),
                minWidth: theme.spacing(6),
                background: theme.palette.primary.main
              }}
            >
              <RefreshIcon htmlColor={theme.colors.primary.lighter} />
            </Button>
          </TooltipCustomize>
        </Box>
      </FilterBox>
    </CustomBox>
  );
}

export default BottomHeader;
